import { css } from '@emotion/react';
import { tokens, applyFont, media } from '@domain-group/fe-brary';
import { STAR_RATING_SECTION } from '.';

const { colors, spacing, typography } = tokens;

export const heroStickyColor = colors.neutral.subdued.default;
export const heavyNeutralColor = colors.neutral.heavy.default;
export const gold = colors.warning.base.default;
export const heroNeutralColor = colors.neutral.medium.default;

export const starDimensions = {
  hero: { height: 12, marginBottom: 12 },
  agentCards: { height: 12, marginBottom: 14 },
  agentCardsNeutral: { height: 12, marginBottom: 14 },
  agentCardsDark: { height: 12, marginBottom: 14 },
  recommendations: { height: 12, marginBottom: 12 },
  sticky: { height: 12, marginBottom: 12 },
  sidebar: {
    height: 12,
    marginBottom: 14,
  },
  agencyPropertySummary: {
    height: 12,
    marginBottom: 11,
  },
  agencyCard: { height: 12, marginBottom: 14 },
  mobile: { height: 12, marginBottom: 12 },
};

const isHero = (section: STAR_RATING_SECTION) =>
  section === STAR_RATING_SECTION.HERO;

const isRecommendations = (section: STAR_RATING_SECTION) =>
  section === STAR_RATING_SECTION.RECOMMENDATIONS;

const isSticky = (section: STAR_RATING_SECTION) =>
  section === STAR_RATING_SECTION.STICKY;

const isNeutral = (section: STAR_RATING_SECTION) =>
  section === STAR_RATING_SECTION.AGENT_CARDS_NEUTRAL;

const isAgencyCard = (section: STAR_RATING_SECTION) =>
  section === STAR_RATING_SECTION.AGENCY_CARD;

const isAgentProfileCard = (section: STAR_RATING_SECTION) =>
  section === STAR_RATING_SECTION.AGENT_CARDS;

const isSidebar = (section: STAR_RATING_SECTION) =>
  section === STAR_RATING_SECTION.SIDEBAR;

const isAgencyPropertySummary = (section: STAR_RATING_SECTION) =>
  section === STAR_RATING_SECTION.AGENCY_PROPERTY_SUMMARY;

const isMobile = (section: STAR_RATING_SECTION) =>
  section === STAR_RATING_SECTION.MOBILE;

export const container = (section: STAR_RATING_SECTION) => {
  let paddingBottom = '0';
  let paddingTop = '0';

  if (isHero(section)) {
    paddingBottom = spacing.none;
    paddingTop = spacing.xs;
  } else if (isRecommendations(section)) {
    paddingBottom = '2px';
  }

  return css`
    padding-top: ${paddingTop};
    padding-bottom: ${paddingBottom};
    display: flex;
    flex-wrap: wrap;
  `;
};

export const avgRating = (section: STAR_RATING_SECTION) => {
  if (isRecommendations(section)) {
    return css`
      ${applyFont(typography.bodyBold)}
    `;
  }

  if (isSticky(section)) {
    return css`
      ${applyFont(typography.bodyBold)}
      color: ${heroStickyColor};
    `;
  }

  if (isNeutral(section)) {
    return css`
      ${applyFont(typography.bodyBold)}
      color: ${heavyNeutralColor};
    `;
  }

  if (isAgencyPropertySummary(section)) {
    return css`
      ${applyFont(typography.bodyBold)}
      color: ${heavyNeutralColor};
    `;
  }

  if (isMobile(section)) {
    return css`
      ${applyFont(typography.miniBold)}
      color: ${heavyNeutralColor};
      font-size: 16px;
    `;
  }

  const color = isHero(section) ? heroStickyColor : heavyNeutralColor;
  const font = isHero(section) ? typography.bodyBold : typography.captionBold;

  return css`
    color: ${color};
    ${applyFont(font)};
  `;
};

export const starRating = (section: STAR_RATING_SECTION) => {
  if (isRecommendations(section)) {
    return css`
      white-space: nowrap;
      margin-bottom: -${starDimensions.recommendations.marginBottom}px;
    `;
  }
  if (isHero(section)) {
    return css`
      margin-bottom: -${starDimensions.hero.marginBottom}px;
    `;
  }
  if (isSticky(section)) {
    return css`
      margin-bottom: -${starDimensions.sticky.marginBottom}px;
    `;
  }
  if (isAgencyPropertySummary(section)) {
    return css`
      margin-bottom: -${starDimensions.agencyPropertySummary.marginBottom}px;
    `;
  }

  if (isMobile(section)) {
    return css`
      margin-bottom: -${starDimensions.mobile.marginBottom}px;
    `;
  }

  return css`
    margin-bottom: -${starDimensions.agentCards.marginBottom}px;
  `;
};

export const totalReview = (section: STAR_RATING_SECTION) => {
  if (isRecommendations(section)) {
    return css`
      ${applyFont(typography.lead)}
      color: ${colors.neutral.heavy.default};
      white-space: nowrap;
      line-height: 35px;
      position: relative;
      top: 1px;
    `;
  }

  if (isSticky(section)) {
    return css`
      ${applyFont(typography.caption)}
      font-size: 14px;
      line-height: 22px;
      color: ${heroStickyColor};
      text-decoration: underline;
    `;
  }

  if (isHero(section)) {
    return css`
      color: ${heroStickyColor};
      ${applyFont(typography.caption)};
      text-decoration: underline;
      margin-bottom: 2px;

      a {
        cursor: pointer;
      }

      ${media('<=tablet')} {
        ${applyFont(typography.body)};
      }
    `;
  }
  if (isAgencyPropertySummary(section)) {
    return css`
      ${applyFont(typography.body)}
      color: ${heavyNeutralColor};
      white-space: nowrap;
      line-height: 10px;
      position: relative;
      top: 1px;
      margin-bottom: 8px;

      ${media('<tablet')} {
        top: 3px;
      }

      a {
        cursor: pointer;
      }
    `;
  }
  if (isAgencyCard(section)) {
    return css`
      color: ${heavyNeutralColor};
      ${applyFont(typography.caption)};
      text-decoration: none;
    `;
  }

  if (isMobile(section)) {
    return css`
      color: ${heavyNeutralColor};
      ${applyFont(typography.mini)};
      white-space: nowrap;
      @media (min-width: 380px) {
        width: 100%;
        text-align: center;
      }
    `;
  }
  // not hero or sticky or recommendations
  return css`
    color: ${isNeutral(section) ? heroNeutralColor : heavyNeutralColor};
    ${applyFont(typography.caption)};
    text-decoration: ${isAgentProfileCard(section) || isSidebar(section)
      ? 'none'
      : 'underline'};

    a {
      cursor: pointer;
    }

    ${media('<desktop')} {
      text-decoration: ${isAgentProfileCard(section) ? 'none' : 'underline'};
    }
  `;
};

export const ratings = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xxs};
`;

export const scrollToReview = (scrollTo = false) => css`
  cursor: ${scrollTo ? 'pointer' : 'default'};
  text-decoration: ${scrollTo ? 'underline' : 'none'};
`;

export const starAndReviews = (section: STAR_RATING_SECTION) => css`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xxs};
  white-space: nowrap;
  margin-top: ${section === STAR_RATING_SECTION.STICKY ? '-2px' : '2px'};
`;

export const seperator = (section: STAR_RATING_SECTION) => {
  if (section === STAR_RATING_SECTION.MOBILE) {
    return css`
      @media (min-width: 380px) {
        display: none;
      }
    `;
  }
  return css``;
};
